import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import { checkAuthAccess, authErrors, resetAuthErrors } from '../api';
import { auth } from '../firebase';
import Alert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';

// Global error handler to suppress 403 error messages in the console
if (typeof window !== 'undefined') {
  // Keep track of the original error handler
  const originalOnError = window.onerror;
  
  // Install our custom error handler
  window.onerror = function(message, source, lineno, colno, error) {
    // Check if this is a 403 error from our API
    if (message && message.toString().includes('403') && 
        message.toString().includes('Request failed')) {
      console.log('Suppressing 403 error in console:', message);
      return true; // Prevents the error from showing in the console
    }
    
    // Call original handler for other errors
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    
    return false; // Let other errors propagate to the console
  };
}

export default function Login() {
  const theme = useTheme();
  const { user, login, logout, forceRender } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showLocalError, setShowLocalError] = useState(false);
  const authCheckAttempted = useRef(false);

  // This effect runs when auth errors change or component mounts
  // It ensures we always use the latest error state
  useEffect(() => {
    console.log("Login component effect - checking auth errors:", 
      {error: authErrors.whitelistError, message: authErrors.message, user: user?.email});
    
    if (authErrors.whitelistError) {
      setErrorMessage(authErrors.message || 'Your email is not authorized to access this application.');
      setShowLocalError(true);
    }
  }, [forceRender, user, authErrors.whitelistError, authErrors.message]);

  // This effect runs when the user changes - it checks auth access again if needed
  useEffect(() => {
    // Only run this if the user is logged in and we haven't checked auth yet
    if (user && !authCheckAttempted.current && !authErrors.whitelistError) {
      console.log("User detected, performing auth check:", user.email);
      authCheckAttempted.current = true;
      
      // Check if this user has access
      const checkAccess = async () => {
        setCheckingAuth(true);
        try {
          const result = await checkAuthAccess();
          console.log("Initial auth check result:", result);
          
          if (!result.authorized && result.whitelisted === false) {
            setErrorMessage('Your email is not authorized to access this application. Please contact the administrator.');
            setShowLocalError(true);
          }
        } catch (err) {
          console.error("Error in initial auth check:", err);
        } finally {
          setCheckingAuth(false);
        }
      };
      
      // Run the check
      checkAccess();
    }
    
    // Reset the check flag when user changes
    if (!user) {
      authCheckAttempted.current = false;
    }
  }, [user]);

  // Handle the login process with immediate auth check
  const handleLogin = async () => {
    setLoading(true);
    setShowLocalError(false);
    authCheckAttempted.current = false;
    
    try {
      // Perform the login
      await login();
      
      // Wait briefly to ensure auth state is updated
      setTimeout(async () => {
        // Check if the user is now logged in
        if (auth.currentUser) {
          setCheckingAuth(true);
          
          try {
            // Check if the user has access - this will update authErrors if needed
            const result = await checkAuthAccess();
            console.log("Auth check result:", result);
            
            // If not authorized, show local error
            if (!result.authorized && result.whitelisted === false) {
              setErrorMessage('Your email is not authorized to access this application. Please contact the administrator.');
              setShowLocalError(true);
            }
          } catch (error) {
            console.error("Error checking auth:", error);
            setErrorMessage('Error checking authorization. Please try again.');
            setShowLocalError(true);
          } finally {
            // Done checking
            setCheckingAuth(false);
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      }, 500);
    } catch (error) {
      console.error('Login error:', error);
      setLoading(false);
      setErrorMessage('An error occurred during sign in. Please try again.');
      setShowLocalError(true);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    // Reset states before logout
    authCheckAttempted.current = false;
    await logout();
    setShowLocalError(false);
    resetAuthErrors();
  };

  // Always check if we should show error from auth errors object
  // This ensures we catch errors set by other components
  const shouldShowError = showLocalError || authErrors.whitelistError;

  // If already logged in and allowed, redirect to dashboard
  if (user && !loading && !checkingAuth && !shouldShowError) {
    return <Navigate to="/" />;
  }

  return (
    <Container maxWidth="sm" sx={{ minHeight: '80vh', display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%' }}>
        <Paper elevation={3} sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'linear-gradient(180deg, rgba(123,50,199,0.05) 0%, rgba(149,62,241,0.05) 100%)' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome to Bidsy Analytics
          </Typography>
          
          {/* Error message */}
          {shouldShowError && (
            <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
              {errorMessage || authErrors.message}
            </Alert>
          )}
          
          {/* User email display */}
          {user && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Signed in as: {user.email}
            </Typography>
          )}
          
          <Typography variant="body1" gutterBottom sx={{ mb: 4 }}>
            {shouldShowError && user ? 
              'Please sign out and try with a different account:' :
              'Please sign in to access the analytics dashboard'
            }
          </Typography>
          
          {!user || shouldShowError ? (
            <Button 
              variant="contained" 
              onClick={shouldShowError && user ? handleLogout : handleLogin}
              disabled={loading || checkingAuth}
              sx={{ 
                bgcolor: shouldShowError ? '#f44336' : theme.palette.primary.main,
                '&:hover': { bgcolor: shouldShowError ? '#d32f2f' : theme.palette.secondary.main },
                py: 1.5,
                px: 3  
              }}
            >
              {loading || checkingAuth ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                  {checkingAuth ? 'Verifying Access...' : 'Signing in...'}
                </Box>
              ) : shouldShowError ? (
                'Sign Out & Try Another Account'
              ) : (
                'Sign in with Google'
              )}
            </Button>
          ) : (
            <Typography variant="body2" color="primary">
              Redirecting to dashboard...
            </Typography>
          )}
        </Paper>
      </Box>
    </Container>
  );
}